import React, { useCallback, useMemo } from 'react';
import styles from './TopPosts.module.css';
import Skeleton from '../../../../../../../ui/Skeleton/Skeleton';
import TopPostsTable, { TiktokTopPostsRow } from './TopPostsTable/TopPostsTable';
import { numberWithCommas } from '../../../../../../../helpers';
import { GetTiktokAudioPostStatsSortableKeys } from '@round/api';
import { OrderByParam } from '../../../../../../../Hooks/useReactTableSortToOrderBy';
import {
    convertOrderByToOrdering,
    convertOrderingToOrderBy,
} from '../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable.helpers';

type Props = {
    loading: boolean;
    errorLoading: string | null;
    topPosts: TiktokTopPostsRow[];
    count: number;
    page: number;
    pageSize: number;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    ordering: GetTiktokAudioPostStatsSortableKeys | undefined;
    setOrdering: (ordering: GetTiktokAudioPostStatsSortableKeys) => void;
};

const TopPosts = ({
    loading,
    errorLoading,
    topPosts,
    count,
    page,
    pageSize,
    setPage,
    setPageSize,
    ordering,
    setOrdering,
}: Props) => {
    const topPostsNoDataLabel = errorLoading ?? 'No top posts found';

    const topPostsRecordsShowingLabel = `displaying ${topPosts.length} post${
        topPosts.length === 1 ? '' : 's'
    } out of ${numberWithCommas(count)} analysed`;

    const orderBy = useMemo<OrderByParam<TiktokTopPostsRow>>(() => convertOrderingToOrderBy(ordering), [ordering]);

    const onOrderByChange = useCallback(
        (orderBy: OrderByParam<TiktokTopPostsRow>) => {
            const ordering = convertOrderByToOrdering(orderBy);
            setOrdering(ordering as GetTiktokAudioPostStatsSortableKeys);
        },
        [setOrdering]
    );

    return (
        <>
            <div className={styles.topPostsHeader}>
                <p className={styles.topPostsTitle}>Top posts</p>
                <p className={styles.topPostsSubtitle}>
                    {loading ? <Skeleton width="20rem" /> : topPostsRecordsShowingLabel}
                </p>
            </div>
            <TopPostsTable
                loading={loading}
                rows={topPosts}
                count={count}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(pageSize) => setPageSize(pageSize ?? 10)}
                orderBy={orderBy}
                onOrderByChange={onOrderByChange}
                noDataLabel={topPostsNoDataLabel}
            />
        </>
    );
};

export default TopPosts;
